/**
 * Top-bar **Profile switcher** (item 3, 2026-06-12; REQ-UI-073 / 074 / 075).
 *
 * Replaces the static "Profile: «name»" status line with a click-to-open
 * dropdown that lists every visible Profile and lets the operator **quick-switch**
 * without opening the gear → Profiles submenu. The trigger still shows the active
 * Profile + the "(modified)" marker (or "Custom (unsaved)" when none is applied);
 * selecting a different Profile applies it via the shared :func:`useApplyProfile`
 * — the SAME apply-by-value + layout-restore path the gear menu's Apply uses, so
 * the two surfaces are identical (no duplicated apply logic; CLAUDE.md §4.4).
 *
 * Switch behaviour: applying **silently** replaces the live working state
 * (architect 2026-06-12 — matches the gear-menu Apply, no confirm). Unsaved
 * working-state overrides are discarded by the apply, exactly as Apply already
 * does. A failed apply (e.g. the Profile vanished — a rare 404) is a no-op: the
 * active pointer is untouched, so the trigger keeps showing the current Profile
 * and the operator sees the switch did not take (fail-safe; the gear menu remains
 * the surface that surfaces apply errors inline).
 *
 * Built on the shadcn ``DropdownMenu`` + ``RadioGroup`` wrapper
 * (:file:`@/components/ui/dropdown-menu`): the active Profile is the checked
 * radio (a non-colour check glyph marks it — REQ-UI-046/094), arrow-key roving
 * focus + type-ahead come for free (REQ-UI-059).
 *
 * Cross-references:
 *   - REQ-UI-073 / 075 (apply by value + layout); REQ-UI-074 ("(modified)").
 *   - REQ-UI-059 / 094 (keyboard + labelled affordances).
 *   - profiles/use-apply-profile.ts (the shared apply path).
 *   - chrome/top-bar.tsx (mount point); preferences/preferences-menu.tsx (the
 *     gear's Apply, which shares the apply hook + offers manage/save).
 */
import { useCallback } from "react";
import { CaretDown } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { usePreferences } from "@/preferences/preferences-context";
import { useWorkingStateModified } from "@/preferences/use-working-state-modified";
import { useApplyProfile } from "@/profiles/use-apply-profile";
import { useProfiles } from "@/profiles/profiles-context";

/**
 * The top-bar active-Profile control: a dropdown showing the active Profile +
 * "(modified)" marker that quick-switches to any visible Profile on select.
 */
export function ProfileSwitcher(): React.ReactElement {
  const { workingState, activeProfile } = usePreferences();
  const modified = useWorkingStateModified(workingState, activeProfile);
  const { profiles } = useProfiles();
  const { applyProfile } = useApplyProfile();

  // Quick-switch: apply the chosen Profile (silently — architect 2026-06-12).
  // Skip a no-op re-apply of the already-active Profile (Radix won't fire on an
  // unchanged value either; this is belt-and-suspenders + avoids a needless
  // re-fly). A failed apply leaves the active pointer untouched (fail-safe).
  const handleSwitch = useCallback(
    (profileId: string): void => {
      if (profileId === activeProfile?.profile_id) {
        return;
      }
      void applyProfile(profileId);
    },
    [activeProfile, applyProfile],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="top-bar-active-profile"
          data-modified={activeProfile !== null && modified ? "true" : undefined}
          aria-label="Active profile — switch profile"
          className={cn(
            "flex max-w-[16rem] items-center gap-1 rounded px-1.5 py-0.5 text-body-sm",
            "hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none",
          )}
        >
          {activeProfile === null ? (
            <span className="text-text-muted">Custom (unsaved)</span>
          ) : (
            <span className="truncate text-text-secondary" title={activeProfile.name}>
              Profile:{" "}
              <span className="text-text-primary" data-testid="top-bar-active-profile-name">
                {activeProfile.name}
              </span>
              {modified && (
                <span
                  className="ml-1 text-text-muted"
                  data-testid="top-bar-active-profile-modified"
                >
                  (modified)
                </span>
              )}
            </span>
          )}
          <CaretDown size={12} weight="bold" aria-hidden className="shrink-0 text-text-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="top-bar-profile-menu">
        <DropdownMenuLabel>Switch profile</DropdownMenuLabel>
        {profiles.length === 0 ? (
          <div
            className="px-2 py-1.5 text-body-sm text-text-muted"
            data-testid="top-bar-profile-menu-empty"
          >
            No saved profiles
          </div>
        ) : (
          <DropdownMenuRadioGroup
            value={activeProfile?.profile_id ?? ""}
            onValueChange={handleSwitch}
          >
            {profiles.map((profile) => (
              <DropdownMenuRadioItem
                key={profile.profile_id}
                value={profile.profile_id}
                data-testid={`top-bar-profile-option-${profile.profile_id}`}
              >
                <span className="truncate">{profile.name}</span>
                {profile.scope === "global" && (
                  <span className="ml-2 shrink-0 text-meta text-text-muted">Shared</span>
                )}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
